import type { UseFormReturn } from 'react-hook-form';

// URL segment of the section-creation step. Reaching it (e.g. clicking "Create"
// on a screen) is a sub-step of the layout create/edit workflow, not a genuine
// exit — see PATHS.ASSEMBLY_LAYOUTS_CREATE_SECTIONS.
export const SECTION_CREATION_PATH = 'assemblies/layouts/create';

export const isSectionCreationPath = (nextPath?: string): boolean =>
  Boolean(nextPath?.includes(SECTION_CREATION_PATH));

// sessionStorage (not localStorage) is used on purpose: the draft only needs to
// bridge the page remount within the current tab and must not resurface in a
// later, unrelated browser session.
export const getLayoutDraftStorageKey = (layoutId?: number | string): string =>
  `layout-draft-${layoutId ?? ''}`;

// Persists the currently unsaved (dirty) fields so they survive the remount that
// happens when the user steps into the section-creation page.
export const persistLayoutDraft = (
  layoutId: number | string | undefined,
  formMethods: UseFormReturn<any>,
): void => {
  const { dirtyFields } = formMethods.formState;
  const values = formMethods.getValues();

  const draft = Object.keys(dirtyFields).reduce<Record<string, unknown>>(
    (acc, key) => ({ ...acc, [key]: values[key] }),
    {},
  );

  sessionStorage.setItem(
    getLayoutDraftStorageKey(layoutId),
    JSON.stringify(draft),
  );
};

// Restores a previously persisted draft (if any) back into the form and clears
// it from storage, so it is consumed exactly once and never leaks into a later
// visit to the same layout.
export const restoreLayoutDraft = (
  layoutId: number | string | undefined,
  formMethods: UseFormReturn<any>,
): void => {
  const storageKey = getLayoutDraftStorageKey(layoutId);
  const rawDraft = sessionStorage.getItem(storageKey);

  if (!rawDraft) return;

  // Consume-once: remove before applying so a corrupted payload cannot get
  // stuck and keep re-throwing on every visit.
  sessionStorage.removeItem(storageKey);

  try {
    const draft = JSON.parse(rawDraft) as Record<string, unknown>;

    Object.entries(draft).forEach(([key, value]) => {
      formMethods.setValue(key, value, { shouldDirty: true });
    });
  } catch {
    // Corrupted draft payload — fall back to the server values.
  }
};
